import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Briefcase, Landmark, Lightbulb, Bell, GraduationCap, SlidersHorizontal, ArrowRight, ArrowLeft } from "lucide-react";

const CONNECT = { icon: Landmark, title: "Connect your UK bank", body: "Link accounts securely through TrueLayer Open Banking. Read-only access means Finlo can never move your money.", cta: "Connect a bank", action: "connect" };

const SLIDES = {
  student: [
    CONNECT,
    { icon: GraduationCap, title: "Your Student Hub", body: "Set a term budget and plan tuition, books, rent and travel so you know how far your loan really stretches.", cta: "Open Student Hub", action: "student" },
    { icon: Bell, title: "Spend alerts that keep you on track", body: "Get a nudge before a category like food or nights out runs past your weekly limit.", cta: "Set budgets", action: "budget" },
    { icon: Lightbulb, title: "Smart savings nudges", body: "We spot small wins in your spending, such as unused subscriptions or cheaper habits, so every pound counts." },
  ],
  professional: [
    CONNECT,
    { icon: Briefcase, title: "Your Professional Hub", body: "See what's safe to spend until payday, track bills before they land, and watch your pension and savings goals grow.", cta: "Open Professional Hub", action: "pro" },
    { icon: SlidersHorizontal, title: "Budgets and subscription radar", body: "Set category limits with rollovers, and let Finlo flag recurring charges quietly draining your balance.", cta: "Set budgets", action: "budget" },
    { icon: Lightbulb, title: "Insights and savings nudges", body: "Track cash flow and spending trends, and get personalised tips to grow your savings each month." },
  ],
};

export default function WelcomeTour({ open, onClose, actions }) {
  const { user, completeTour } = useAuth();
  const slides = SLIDES[user?.profile_type] || SLIDES.professional;
  const [i, setI] = useState(0);
  const s = slides[i];
  const last = i === slides.length - 1;

  useEffect(() => { if (open) setI(0); }, [open]);

  const finish = async (action) => {
    onClose();
    if (!user?.tour_completed) await completeTour().catch(() => {});
    if (action) actions[action]?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish()}>
      <DialogContent data-testid="welcome-tour-modal" className="bg-[#14181D] border-white/10 text-white w-[calc(100%-2rem)] max-w-md rounded-2xl sm:rounded-2xl p-0 overflow-hidden gap-0 [&>button]:hidden">
        <div className="relative h-36 bg-[#0D0D0D] flex items-center justify-center overflow-hidden">
          <div className="absolute h-48 w-48 rounded-full bg-[#00C896]/20 blur-3xl" />
          <AnimatePresence mode="wait">
            <motion.span key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.25 }}
              className="relative h-16 w-16 rounded-2xl bg-[#00C896] text-[#04140D] flex items-center justify-center shadow-[0_0_30px_rgba(0,200,150,0.45)]">
              <s.icon className="h-8 w-8" />
            </motion.span>
          </AnimatePresence>
          <button data-testid="welcome-tour-skip" onClick={() => finish()} className="absolute top-3 right-4 text-xs text-slate-400 hover:text-white transition-colors">Skip</button>
          <span className="absolute top-3 left-4 text-[10px] uppercase tracking-[0.18em] text-[#4DE3BF]">Welcome to Finlo</span>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
              <DialogTitle data-testid="welcome-tour-title" className="font-display text-xl">{s.title}</DialogTitle>
              <DialogDescription className="text-slate-400 mt-2 text-sm leading-relaxed min-h-[60px]">{s.body}</DialogDescription>
              {s.cta && (
                <button data-testid="welcome-tour-cta" onClick={() => finish(s.action)} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4DE3BF] hover:text-[#7FEAD0] transition-colors">
                  {s.cta} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-1.5" data-testid="welcome-tour-dots">
              {slides.map((_, k) => (
                <span key={k} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-6 bg-[#00C896]" : "w-1.5 bg-white/15"}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {i > 0 && (
                <Button data-testid="welcome-tour-back" variant="ghost" size="sm" onClick={() => setI(i - 1)} className="rounded-full text-slate-300 hover:bg-white/5 hover:text-white">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <Button data-testid="welcome-tour-next" size="sm" onClick={() => (last ? finish() : setI(i + 1))} className="rounded-full px-5 bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold">
                {last ? "Get started" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
